import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { WA_PHYSICS } from '../core/constants';

export interface BungeeState {
  active: boolean;
  anchorX: number;
  anchorY: number;
  maxLength: number;
  currentLength: number;
  phase: 'dropping' | 'stretching' | 'retracting';
}

export const WeaponBungee: WeaponDefinition = {
  id: 'bungee',
  name: 'Bungee',
  icon: 'bungee.1.png',
  category: 'utility',
  maxAmmo: 2,
  maxUsesPerTurn: 1,
  powerCharging: false,
  timerAdjustable: false,
  showAimGuide: false,
  allowRetreat: false,

  // Bungee is never "fired" by the player - it auto-activates on falling.
  // This fire() exists only to satisfy the WeaponDefinition interface.
  fire(_ctx: WeaponContext): WeaponResult {
    return {
      success: false,
      message: 'Bungee activates automatically when falling',
    };
  },
};

export function activateBungee(worm: any): void {
  const startX = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
  const startY = worm.position.y;

  const bungeeState: BungeeState = {
    active: true,
    anchorX: startX,
    anchorY: startY,
    maxLength: 250,
    currentLength: 0,
    phase: 'dropping',
  };

  (worm as any).bungeeState = bungeeState;
  worm.velocity.y = 0;
}

export function updateBungeePhysics(worm: any, terrain: any, deltaTime: number): boolean {
  const bungeeState = worm.bungeeState as BungeeState | undefined;
  if (!bungeeState || !bungeeState.active) return false;

  const gravity = WA_PHYSICS.GRAVITY;

  const wormCenterX = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
  const wormY = worm.position.y + WA_PHYSICS.WORM.HEIGHT;

  const dx = wormCenterX - bungeeState.anchorX;
  const dy = wormY - bungeeState.anchorY;
  bungeeState.currentLength = Math.sqrt(dx * dx + dy * dy);

  if (bungeeState.phase === 'dropping') {
    worm.velocity.y += gravity * deltaTime;
    worm.position.y += worm.velocity.y * deltaTime;

    const feetX = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
    const feetY = worm.position.y + WA_PHYSICS.WORM.HEIGHT;

    if (terrain.isSolid(feetX, feetY)) {
      while (terrain.isSolid(feetX, worm.position.y + WA_PHYSICS.WORM.HEIGHT - 1)) {
        worm.position.y -= 1;
      }
      worm.velocity.y = 0;
      worm.isGrounded = true;
      bungeeState.active = false;
      (worm as any).bungeeState = undefined;
      return true;
    } else if (bungeeState.currentLength >= bungeeState.maxLength) {
      bungeeState.phase = 'stretching';
    }
  } else if (bungeeState.phase === 'stretching') {
    const stretch = bungeeState.currentLength - bungeeState.maxLength;
    const springForce = stretch * 5;

    const normalY = (bungeeState.anchorY - wormY) / bungeeState.currentLength;
    worm.velocity.y += (springForce * normalY + gravity * 0.3) * deltaTime;
    worm.position.y += worm.velocity.y * deltaTime;

    if (bungeeState.currentLength < bungeeState.maxLength) {
      bungeeState.phase = 'retracting';
    }
  } else if (bungeeState.phase === 'retracting') {
    worm.velocity.y += gravity * 0.5 * deltaTime;
    worm.position.y += worm.velocity.y * deltaTime;

    if (worm.position.y <= bungeeState.anchorY - WA_PHYSICS.WORM.HEIGHT) {
      worm.position.y = bungeeState.anchorY - WA_PHYSICS.WORM.HEIGHT;
      worm.velocity.y = 0;
      worm.isGrounded = true;
      bungeeState.active = false;
      (worm as any).bungeeState = undefined;
      return true;
    } else if (worm.velocity.y > 0) {
      bungeeState.phase = 'dropping';
    }
  }
  return false;
}

export function isBungeeActive(worm: any): boolean {
  const state = worm.bungeeState as BungeeState | undefined;
  return state?.active === true;
}

export function releaseBungee(worm: any): void {
  const state = worm.bungeeState as BungeeState | undefined;
  if (state) {
    state.active = false;
    (worm as any).bungeeState = undefined;
  }
}

export function getBungeeRenderData(
  worm: any,
): { startX: number; startY: number; endX: number; endY: number } | null {
  const state = worm.bungeeState as BungeeState | undefined;
  if (!state?.active) return null;

  return {
    startX: state.anchorX,
    startY: state.anchorY,
    endX: worm.position.x + WA_PHYSICS.WORM.WIDTH / 2,
    endY: worm.position.y,
  };
}
